/**
 * useApiClient — central HTTP wrapper for `/api/*` requests.
 *
 * Responsibilities:
 *   - `credentials: 'include'` so HttpOnly session cookie auto-attaches.
 *   - Parse `{error:{code,message,details?}}` envelope on 4xx/5xx (per UI-02).
 *   - Throw typed `ApiError` distinguishing network vs HTTP errors.
 *   - Parse `Retry-After` header for 429 responses.
 *   - 401 hook — on session expiry, invoke registered handler so router/store can
 *     redirect to /login with `?expired=1` (per session-3b briefing §5 + §7.4).
 *   - CSRF readiness extension point (deferred MVP-2 per UI-02 §CSRF) — accepts
 *     optional `csrfHeader` config; default no-op.
 *
 * Login endpoint (`/api/auth/login`) — caller passes `{ skipExpiredHook: true }`
 * via init so the 401 lockout/wrong-creds path does NOT trigger the global
 * expired flow (per briefing §5 — login lockout is 401 not 429, but we never
 * mark session expired pre-login).
 */

export interface ApiErrorShape extends Error {
  status: number;
  code?: string;
  details?: unknown;
  retryAfter?: number; // seconds parsed from Retry-After header
  isNetworkError: boolean;
}

export class ApiError extends Error implements ApiErrorShape {
  status: number;
  code?: string;
  details?: unknown;
  retryAfter?: number;
  isNetworkError: boolean;

  constructor(init: {
    status: number;
    message: string;
    code?: string;
    details?: unknown;
    retryAfter?: number;
    isNetworkError?: boolean;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.retryAfter = init.retryAfter;
    this.isNetworkError = init.isNetworkError ?? false;
  }
}

export interface ApiClientRequestInit extends RequestInit {
  /**
   * If true — do NOT trigger the global expired hook on 401. Used by login
   * endpoint where a 401 is a normal flow (wrong creds / lockout), not a
   * session expiry.
   */
  skipExpiredHook?: boolean;
  /**
   * CSRF header to attach (deferred MVP-2 — composable extension point only).
   */
  csrfHeader?: { name: string; value: string };
}

type ExpiredHandler = () => void;

let expiredHandler: ExpiredHandler | null = null;

/**
 * Register a global handler invoked when any non-login `/api/*` request
 * returns 401. Typically called once at app startup (router setup) so the
 * auth store can mark session expired + router redirects to /login.
 */
export function onSessionExpired(handler: ExpiredHandler): void {
  expiredHandler = handler;
}

/**
 * Clear the registered handler — primarily for tests.
 */
export function clearSessionExpiredHandler(): void {
  expiredHandler = null;
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  // Numeric seconds?
  const asInt = Number.parseInt(header, 10);
  if (!Number.isNaN(asInt) && /^\d+$/.test(header.trim())) {
    return asInt;
  }
  // HTTP-date — convert to seconds-from-now (clamped >=0).
  const ts = Date.parse(header);
  if (Number.isNaN(ts)) return undefined;
  const delta = Math.floor((ts - Date.now()) / 1000);
  return delta > 0 ? delta : 0;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

async function parseErrorBody(
  response: Response,
): Promise<{ code?: string; message: string; details?: unknown }> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    return { message: `HTTP ${response.status}` };
  }
  if (body && typeof body === 'object' && 'error' in body) {
    const env = (body as ErrorEnvelope).error ?? {};
    return {
      code: env.code,
      message: env.message ?? `HTTP ${response.status}`,
      details: env.details,
    };
  }
  // Fallback — non-envelope body (older shape / unexpected).
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const message =
      typeof obj.message === 'string'
        ? obj.message
        : typeof obj.error === 'string'
          ? obj.error
          : `HTTP ${response.status}`;
    return { message };
  }
  return { message: `HTTP ${response.status}` };
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  init: ApiClientRequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (body !== undefined && !(body instanceof FormData)) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (init.csrfHeader) {
    headers.set(init.csrfHeader.name, init.csrfHeader.value);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      method,
      headers,
      credentials: 'include',
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  } catch (err) {
    // Network error / fetch reject — TypeError typically.
    throw new ApiError({
      status: 0,
      message:
        err instanceof Error && err.message
          ? err.message
          : 'Network request failed',
      isNetworkError: true,
    });
  }

  if (response.ok) {
    if (response.status === 204) {
      return undefined as unknown as T;
    }
    // Some endpoints may return empty body even on 200 — be defensive.
    const text = await response.text();
    if (!text) return undefined as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError({
        status: response.status,
        message: 'Malformed JSON response',
      });
    }
  }

  const { code, message, details } = await parseErrorBody(response);
  const retryAfter =
    response.status === 429
      ? parseRetryAfter(response.headers.get('Retry-After'))
      : undefined;

  // Session expired hook — only when not opted out by caller.
  if (response.status === 401 && !init.skipExpiredHook && expiredHandler) {
    try {
      expiredHandler();
    } catch {
      /* swallow — user-facing flow already broken */
    }
  }

  throw new ApiError({
    status: response.status,
    code,
    message,
    details,
    retryAfter,
  });
}

export interface ApiClient {
  get<T>(path: string, init?: ApiClientRequestInit): Promise<T>;
  post<T>(
    path: string,
    body?: unknown,
    init?: ApiClientRequestInit,
  ): Promise<T>;
  put<T>(
    path: string,
    body?: unknown,
    init?: ApiClientRequestInit,
  ): Promise<T>;
  /**
   * PATCH — optional на интерфейсе, чтобы существующие partial-моки в тестах
   * (которые конструируют ApiClient без patch) оставались валидными. Реальный
   * useApiClient() всегда предоставляет реализацию.
   */
  patch?<T>(
    path: string,
    body?: unknown,
    init?: ApiClientRequestInit,
  ): Promise<T>;
  delete<T>(path: string, init?: ApiClientRequestInit): Promise<T>;
}

export function useApiClient(): ApiClient {
  return {
    get: <T>(path: string, init?: ApiClientRequestInit) =>
      request<T>('GET', path, undefined, init),
    post: <T>(path: string, body?: unknown, init?: ApiClientRequestInit) =>
      request<T>('POST', path, body, init),
    put: <T>(path: string, body?: unknown, init?: ApiClientRequestInit) =>
      request<T>('PUT', path, body, init),
    patch: <T>(path: string, body?: unknown, init?: ApiClientRequestInit) =>
      request<T>('PATCH', path, body, init),
    delete: <T>(path: string, init?: ApiClientRequestInit) =>
      request<T>('DELETE', path, undefined, init),
  };
}
